/**
 * TLAČ BEZ VYSKAKOVACIEHO OKNA.
 *
 * ── Odkiaľ to prišlo ─────────────────────────────────────────────────────
 * Oto: *„Stále sa nám blokuje v Chrome na baliacom stole vyskakovacie okno."*
 *
 * ── Prečo sa to dialo ────────────────────────────────────────────────────
 * Nie je to náhoda ani zle nastavený prehliadač. Prehliadač povolí otvoriť
 * okno len vtedy, keď o to požiada **stlačenie tlačidla** – a to povolenie
 * platí okamih. Pri baliacom stole medzi kliknutím a tlačou stojí celý
 * rozhovor s kuriérom: zásielka sa založí, vypýta sa štítok, PDF sa stiahne
 * a vykreslí. To trvá sekundy a povolenie dovtedy vyprší. Prehliadač potom
 * vidí okno, ktoré sa otvára „samo od seba" – a zablokuje ho.
 *
 * Preto sa dalo povoliť tisíckrát a stále to vyskakovalo znova: povolenie
 * pre stránku pomôže, ale problém je v tom, že sa okno vôbec otvára.
 *
 * ── Ako sa to rieši ──────────────────────────────────────────────────────
 * Tlačí sa do **rámca vloženého do tej istej stránky** (`iframe`). Nie je to
 * nové okno, takže sa nemá čo blokovať – a zároveň je to samostatný dokument
 * s vlastným `@page`, takže rozmer štítku, okraje aj otočenie platia presne
 * tak ako doteraz.
 *
 * Vedľajší zisk: na obrazovke nič neprebliskne a nič sa nemusí zatvárať.
 * Skladník má ruky na krabici, nie na krížiku okna.
 *
 * Stará cesta ostáva ako **záchrana**: keby rámec v niektorom prehliadači
 * zlyhal, skúsi sa okno. Tlač je to posledné, čo pri stole smie prestať
 * fungovať.
 *
 * ── Prečo sa čaká na obrázky ─────────────────────────────────────────────
 * Štítok je obrázok vykreslený z PDF. Keď sa tlač spustí skôr, než sa
 * obrázok vykreslí, z tlačiarne vyjde **prázdny štítok** – a to je horšie
 * než hláška: papier sa minie, kotúč sa posunie a nikto nevie prečo.
 */

/** Koľko najviac čakáme na vykreslenie obrázkov, kým tlačíme aj tak. */
const NAJVIAC_CAKAT = 6000;

/** Kedy rámec upratať, keby `afterprint` neprišiel (systémový dialóg). */
const POISTKA = 60000;

/**
 * Vytlačí hotový HTML dokument.
 *
 * @param  {string} html  celý dokument aj so štýlmi a `@page`
 * @return {boolean}      či sa tlač podarilo spustiť
 */
export function tlacHtml(html) {
  return vRamci(html) || vOkne(html);
}

/* ---------------------------------------------------------------- rámec */

function vRamci(html) {
  try {
    if (typeof document === 'undefined' || !document.body) return false;
    const ram = document.createElement('iframe');
    ram.setAttribute('aria-hidden', 'true');
    ram.title = 'Tlač';
    /* Mimo obrazovky, ale **s rozmermi**. Rámec bez veľkosti alebo skrytý
     * cez `display:none` sa nemá ako rozložiť a vyšiel by prázdny list. */
    ram.style.cssText = 'position:fixed;left:-10000px;top:0;width:900px;height:1200px;border:0';
    document.body.appendChild(ram);

    const d = ram.contentDocument;
    if (!d) { ram.remove(); return false; }
    d.open();
    d.write(html);
    d.close();

    let uprataneRaz = false;
    const uprac = () => {
      if (uprataneRaz) return;
      uprataneRaz = true;
      /* Nie hneď: tlač sa odosiela ešte chvíľu po `afterprint` a rámec
       * zrušený priskoro ju vie zahodiť. */
      setTimeout(() => { try { ram.remove(); } catch { /* už je preč */ } }, 1500);
    };

    let tlaceneRaz = false;
    const tlac = () => {
      if (tlaceneRaz) return;
      tlaceneRaz = true;
      const okno = ram.contentWindow;
      if (!okno) { uprac(); return; }
      /* Zmenšenie súpisu sa počíta tesne pred tlačou – vtedy je text zalomený
       * a písma načítané, takže meranie zodpovedá papieru. */
      try { okno.zmestit?.(); } catch { /* nevadí */ }
      try {
        okno.onafterprint = uprac;
        okno.focus();
        setTimeout(uprac, POISTKA);
        okno.print();
      } catch { uprac(); }
    };

    pockajNaObrazky(d).then(tlac);
    return true;
  } catch {
    return false;
  }
}

/**
 * Počká, kým sú obrázky v dokumente vykreslené.
 *
 * Vždy sa vráti – aj keď sa obrázok načítať nepodarí. Netlačiť vôbec by
 * znamenalo, že pri chybe jedného obrázka nevyjde ani súpis.
 */
function pockajNaObrazky(d) {
  const obrazky = [...(d.images || [])].filter((o) => !o.complete);
  if (!obrazky.length) return Promise.resolve();
  return Promise.race([
    Promise.all(obrazky.map((o) => new Promise((hotovo) => {
      o.addEventListener('load', hotovo, { once: true });
      o.addEventListener('error', hotovo, { once: true });
    }))),
    new Promise((hotovo) => { setTimeout(hotovo, NAJVIAC_CAKAT); }),
  ]);
}

/* ----------------------------------------------------------- záchrana */

/**
 * Pôvodná cesta cez samostatné okno.
 *
 * Ostáva tu zámerne: keby rámec niekde nefungoval, tlač sa nesmie zastaviť.
 * Práve toto okno prehliadač pri stole blokoval, takže sa už používa len
 * vtedy, keď iná možnosť nie je.
 */
function vOkne(html) {
  try {
    const okno = window.open('', '_blank', 'width=680,height=860');
    if (!okno) return false;
    okno.document.write(html);
    okno.document.close();

    let uzTlacene = false;
    const tlac = () => {
      if (uzTlacene) return;
      uzTlacene = true;
      try { okno.zmestit?.(); } catch { /* nevadí */ }
      let uzZavrete = false;
      const zavri = () => {
        if (uzZavrete) return;
        uzZavrete = true;
        setTimeout(() => { try { okno.close(); } catch { /* nevadí */ } }, 300);
      };
      okno.onafterprint = zavri;
      const poistka = setTimeout(zavri, POISTKA);
      try { okno.focus(); okno.print(); } catch { clearTimeout(poistka); zavri(); }
    };
    okno.onload = tlac;
    setTimeout(tlac, 800);
    return true;
  } catch {
    return false;
  }
}
